// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Which embedding model you are chunking FOR.
 *
 * This list is not a menu of models chunkforge runs — it runs none of them. It
 * is a list of tokenizers, because the only thing a chunk size means is "this
 * many of that model's tokens", and models disagree: the same paragraph is 1,024
 * tokens to one and 1,180 to another. Picking the wrong one produces a config
 * that silently overflows the model's input limit.
 *
 * Every entry below was checked from a browser origin rather than copied from a
 * model card. `curl -I -H 'Origin: https://chunkforge.benrichardson.dev'` against
 * each `resolve/main/tokenizer.json` on 2026-08-10 returned 200 with an
 * `access-control-allow-origin` echoing the origin, so each of these is fetchable
 * from a static page with no proxy. The gated repos are named too, because
 * "meta-llama/* returns 401 to an anonymous request" is a fact a user is better
 * off reading here than discovering as a failed download.
 *
 * `maxTokens` is the model's documented input limit. It is here because
 * exceeding it is the single most common silent failure in a RAG pipeline: the
 * embedding API does not error, it truncates, and the tail of every oversized
 * chunk is simply not in the vector.
 */

export type TokenizerKind = 'wordpiece' | 'bpe';

export interface ModelEntry {
  id: string;
  label: string;
  /** Hugging Face repo whose `tokenizer.json` defines this model's tokens. */
  repo: string;
  kind: TokenizerKind;
  /** Measured with a HEAD from a third-party origin on 2026-08-10. */
  bytes: number;
  /** Documented maximum input length in tokens. */
  maxTokens: number;
  note?: string;
}

/**
 * The static retrieval model chunkforge scores with ships the very same
 * `tokenizer.json` as the BGE family — SHA-256
 * d241a60d5e8f04cc1b2b3e9ef7a4921b27bf526d9f6050ab90f9267a1f9e5c66 for both, so
 * choosing a BGE model costs no download at all.
 */
export const SHARED_WORDPIECE_SHA = 'd241a60d5e8f04cc1b2b3e9ef7a4921b27bf526d9f6050ab90f9267a1f9e5c66';

export const MODELS: ModelEntry[] = [
  {
    id: 'bge-small-en-v1.5',
    label: 'BAAI bge-small-en-v1.5',
    repo: 'BAAI/bge-small-en-v1.5',
    kind: 'wordpiece',
    bytes: 711_396,
    maxTokens: 512,
    note: 'Same tokenizer file as the scorer already uses — nothing to download.',
  },
  {
    id: 'bge-base-en-v1.5',
    label: 'BAAI bge-base-en-v1.5',
    repo: 'BAAI/bge-base-en-v1.5',
    kind: 'wordpiece',
    bytes: 711_396,
    maxTokens: 512,
  },
  {
    id: 'bge-large-en-v1.5',
    label: 'BAAI bge-large-en-v1.5',
    repo: 'BAAI/bge-large-en-v1.5',
    kind: 'wordpiece',
    bytes: 711_396,
    maxTokens: 512,
  },
  {
    id: 'all-MiniLM-L6-v2',
    label: 'sentence-transformers all-MiniLM-L6-v2',
    repo: 'sentence-transformers/all-MiniLM-L6-v2',
    kind: 'wordpiece',
    bytes: 466_247,
    maxTokens: 256,
    note: 'Its documented sequence length is 256, not 512 — chunks above that are truncated.',
  },
  {
    id: 'all-mpnet-base-v2',
    label: 'sentence-transformers all-mpnet-base-v2',
    repo: 'sentence-transformers/all-mpnet-base-v2',
    kind: 'wordpiece',
    bytes: 466_021,
    maxTokens: 384,
  },
  {
    id: 'gte-small',
    label: 'thenlper gte-small',
    repo: 'thenlper/gte-small',
    kind: 'wordpiece',
    bytes: 711_661,
    maxTokens: 512,
  },
  {
    id: 'e5-base-v2',
    label: 'intfloat e5-base-v2',
    repo: 'intfloat/e5-base-v2',
    kind: 'wordpiece',
    bytes: 711_396,
    maxTokens: 512,
  },
  {
    id: 'nomic-embed-text-v1.5',
    label: 'nomic-ai nomic-embed-text-v1.5',
    repo: 'nomic-ai/nomic-embed-text-v1.5',
    kind: 'wordpiece',
    bytes: 711_396,
    maxTokens: 8192,
  },
  {
    id: 'mxbai-embed-large-v1',
    label: 'mixedbread mxbai-embed-large-v1',
    repo: 'mixedbread-ai/mxbai-embed-large-v1',
    kind: 'wordpiece',
    bytes: 711_396,
    maxTokens: 512,
  },
  {
    id: 'jina-embeddings-v2-base-en',
    label: 'jina-embeddings-v2-base-en',
    repo: 'jinaai/jina-embeddings-v2-base-en',
    kind: 'wordpiece',
    bytes: 711_577,
    maxTokens: 8192,
  },
  {
    id: 'openai-cl100k',
    label: 'OpenAI text-embedding-3-small / -3-large / ada-002',
    repo: 'Xenova/text-embedding-ada-002',
    kind: 'bpe',
    bytes: 4_233_346,
    maxTokens: 8191,
    note: 'cl100k_base. Verified token-for-token against gpt-tokenizer on a 11,003-token document.',
  },
  {
    id: 'openai-o200k',
    label: 'OpenAI o200k (GPT-4o family context)',
    repo: 'Xenova/gpt-4o',
    kind: 'bpe',
    bytes: 9_729_051,
    maxTokens: 128_000,
    note: 'For sizing chunks against a generation context rather than an embedder.',
  },
  {
    id: 'gte-modernbert-base',
    label: 'Alibaba-NLP gte-modernbert-base',
    repo: 'Alibaba-NLP/gte-modernbert-base',
    kind: 'bpe',
    bytes: 3_583_228,
    maxTokens: 8192,
  },
  {
    id: 'qwen3-embedding-0.6b',
    label: 'Qwen3-Embedding-0.6B',
    repo: 'Qwen/Qwen3-Embedding-0.6B',
    kind: 'bpe',
    bytes: 11_423_705,
    maxTokens: 32_768,
  },
];

/**
 * Models chunkforge deliberately does NOT offer, and why.
 *
 * Naming them is the point. A tokenizer this tool cannot reproduce exactly would
 * produce a chunk size that is wrong in the user's pipeline, and a wrong number
 * delivered confidently is worse than an absent one.
 */
export const UNSUPPORTED: { label: string; reason: string }[] = [
  {
    label: 'multilingual-e5, LaBSE, and other SentencePiece models',
    reason:
      'Their tokenizer.json is a Unigram model with a Metaspace pre-tokenizer. chunkforge implements WordPiece and byte-level BPE exactly and does not approximate a third.',
  },
  {
    label: 'Llama, Gemma and other gated repositories',
    reason:
      'meta-llama/* and google/gemma-* return HTTP 401 to an anonymous request, so their tokenizer cannot be fetched from a page with no account behind it.',
  },
  {
    label: 'Cohere Embed and Voyage',
    reason: 'Neither publishes a tokenizer that can run locally, so any count for them would be an estimate wearing a decimal point.',
  },
];

export const DEFAULT_MODEL = 'bge-small-en-v1.5';

export function modelById(id: string): ModelEntry {
  return MODELS.find((m) => m.id === id) ?? MODELS[0];
}

export function tokenizerUrl(m: ModelEntry): string {
  return `https://huggingface.co/${m.repo}/resolve/main/tokenizer.json`;
}
